import { useCallback, memo } from 'react'
import { replace } from 'connected-react-router'

import { useMutation } from '@tanstack/react-query'

import Routes from '@tabeeb/routes'
import { useDispatch } from '@tabeeb/store'
import CircularProgressBar from '@tabeeb/modules/shared/tabeebCircularProgressBar'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import { useAnnouncement } from '@tabeeb/modules/articles/hooks'
import { useApi } from '@tabeeb/modules/shared/utils/API'
import { AnnouncementCreateRequest } from '@tabeeb/contracts/microservices/swagger-article-service'

import AnnouncementEditorPage from '../AnnouncementEditorPage'

export interface AnnouncementEditPageProps {
  id: number
}

const AnnouncementEditPage: React.FC<AnnouncementEditPageProps> = ({ id }) => {
  const dispatch = useDispatch()

  const { articleServiceApi: api } = useApi()

  const onError = useCallback(() => {
    dispatch(onAddErrorNotification({ message: 'Failed to load announcement' }))
    dispatch(replace(Routes.announcements))
  }, [dispatch])

  const updateMutation = useMutation({
    mutationFn: (request: AnnouncementCreateRequest) => api.announcements.updateAsync(id, request),
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Announcement successfully updated' }))
      dispatch(replace(Routes.announcements))
    },
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to update announcement' }))
    },
  })

  const { loading, announcement } = useAnnouncement({ id, onError })
  if (loading || !announcement) {
    return <CircularProgressBar />
  }

  return <AnnouncementEditorPage title='Edit announcement' initial={announcement} onSubmit={updateMutation.mutate} />
}

export default memo(AnnouncementEditPage)
